import React, { useCallback, useEffect, useMemo } from 'react';

import { isEmptyCollection, SiteConfigManager, useCasinoNewState } from '@atlas/core';
import { CASINO_NAVIGATION_TYPES, CasinoProviders } from '@atlas/ui';

import { useCasinoFilterState } from 'components/Casino/CasinoFilterContext';
import { getSearchParam } from 'src/helpers/getSearchParam';

import {
  casinoFilterTypeMap,
  deleteCasinoFilter,
  toggleCasinoFilterListItem,
} from '../useCasinoFilter';

const allProvidersLabel = 'all_providers';

const ProvidersFilter = () => {
  const {
    providers: { data: providers },
  } = useCasinoNewState();
  const { filter, updateFilter } = useCasinoFilterState();
  const homePageFunctionality = SiteConfigManager.getConfig(
    'config.config.casino.desktop_home_page',
  );
  const navigationType = homePageFunctionality
    ? SiteConfigManager.getConfig('config.config.general.desktopCasinoNavigationView') ||
      CASINO_NAVIGATION_TYPES.NAVIGATION1
    : CASINO_NAVIGATION_TYPES.NAVIGATION8;
  const allProvidersActive = isEmptyCollection(filter.providerNames);
  const searchProviderNames = getSearchParam(window.location.search, 'providerNames');

  const handleClearAll = useCallback(() => {
    if (allProvidersActive) return;

    updateFilter({ providerNames: [] });
    deleteCasinoFilter(casinoFilterTypeMap.providerNames);
  }, [allProvidersActive, updateFilter]);

  // set initial active filter providerNames from search param on mount
  useEffect(() => {
    const filtersFromSearch = searchProviderNames?.split(',');
    if (filtersFromSearch?.length > 0) {
      updateFilter({ providerNames: filtersFromSearch });
    }
  }, []);

  const handleClick = useCallback(
    name => {
      if (filter.providerNames.includes(name)) {
        const newArr = filter.providerNames.filter(el => el !== name);
        updateFilter({ providerNames: newArr });
      } else {
        updateFilter({ providerNames: [...filter.providerNames, name] });
      }
      toggleCasinoFilterListItem(casinoFilterTypeMap.providerNames, name);
    },
    [filter.providerNames, updateFilter],
  );

  const allProviders = useMemo(
    () => [
      {
        active: allProvidersActive,
        id: allProvidersLabel,
        name: allProvidersLabel,
        onClick: handleClearAll,
      },
      ...providers.map(({ id, name }) => ({
        active: filter.providerNames.includes(name),
        id,
        name,
        onClick: handleClick,
      })),
    ],
    [allProvidersActive, handleClearAll, providers, filter.providerNames, handleClick],
  );

  return (
    <CasinoProviders
      providers={allProviders}
      selectedProviders={filter.providerNames}
      type={navigationType}
    />
  );
};

export default ProvidersFilter;
